import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faDownload, faMobile } from '@fortawesome/free-solid-svg-icons';
import { faApple, faGooglePlay } from '@fortawesome/free-brands-svg-icons';

const slideDown = keyframes`
  from {
    transform: translateY(-100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const slideUp = keyframes`
  from {
    transform: translateY(100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const BannerContainer = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(135deg, #316C6E 0%, #254F51 100%);
  color: white;
  padding: 12px 16px;
  z-index: 1001;
  animation: ${slideUp} 0.5s ease-out;
  box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.15);

  @media (min-width: 768px) {
    display: none;
  }
`;

const BannerContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 400px;
  margin: 0 auto;
  gap: 12px;
`;

const AppInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
`;

const AppIcon = styled.div`
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
`;

const AppText = styled.div`
  flex: 1;
`;

const AppTitle = styled.div`
  font-weight: 600;
  font-size: 14px;
  line-height: 1.2;
`;

const AppSubtitle = styled.div`
  font-size: 12px;
  opacity: 0.9;
  margin-top: 2px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const DownloadButton = styled.a`
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  padding: 8px 12px;
  border-radius: 20px;
  text-decoration: none;
  font-size: 12px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);

  &:hover {
    background: rgba(255, 255, 255, 0.25);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }

  &:active {
    transform: translateY(0);
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.8);
  font-size: 16px;
  padding: 4px;
  cursor: pointer;
  border-radius: 4px;
  transition: all 0.3s ease;

  &:hover {
    color: white;
    background: rgba(255, 255, 255, 0.1);
  }
`;

const MobileAppBanner = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [deviceType, setDeviceType] = useState('');

  useEffect(() => {
    // Check if user is on mobile device
    const checkMobileDevice = () => {
      const userAgent = navigator.userAgent.toLowerCase();
      const isTablet = /tablet|ipad|playbook|silk/i.test(userAgent);
      const isMobile = /mobile|iphone|ipod|android|blackberry|opera|mini|windows\sce|palm|smartphone|iemobile/i.test(userAgent);
      
      if (isTablet || isMobile) {
        // Detect specific OS
        if (/iphone|ipad|ipod/i.test(userAgent)) {
          setDeviceType('ios');
        } else if (/android/i.test(userAgent)) {
          setDeviceType('android');
        } else {
          setDeviceType('mobile');
        }
        
        // Check if banner was dismissed
        const bannerDismissed = localStorage.getItem('mobileAppBannerDismissed');
        const dismissedDate = localStorage.getItem('mobileAppBannerDismissedDate');
        const currentDate = new Date().toDateString();
        
        // Show banner if not dismissed today
        if (!bannerDismissed || dismissedDate !== currentDate) {
          setIsVisible(true);
        }
      }
    };

    checkMobileDevice();
  }, []);

  const handleDismiss = () => {
    setIsVisible(false);
    localStorage.setItem('mobileAppBannerDismissed', 'true');
    localStorage.setItem('mobileAppBannerDismissedDate', new Date().toDateString());
  };

  const getDownloadUrl = () => {
    if (deviceType === 'ios') {
      return 'https://apple.co/44ajr0e';
    } else if (deviceType === 'android') {
      return 'https://bit.ly/QuisqueyaQuest';
    }
    return 'https://bit.ly/QuisqueyaQuest'; // Default to Play Store
  };

  const getStoreIcon = () => {
    if (deviceType === 'ios') {
      return faApple;
    }
    return faGooglePlay;
  };

  const getStoreText = () => {
    if (deviceType === 'ios') {
      return 'App Store';
    }
    return 'Play Store';
  };

  if (!isVisible) return null;

  return (
    <BannerContainer>
      <BannerContent>
        <AppInfo>
          <AppIcon>
            <FontAwesomeIcon icon={faMobile} />
          </AppIcon>
          <AppText>
            <AppTitle>Get Quisqueya Quest App</AppTitle>
            <AppSubtitle>Better experience on mobile</AppSubtitle>
          </AppText>
        </AppInfo>
        <ButtonGroup>
          <DownloadButton 
            href={getDownloadUrl()} 
            target="_blank" 
            rel="noopener noreferrer"
            onClick={() => {
              // Track download click
              if (window.gtag) {
                window.gtag('event', 'mobile_app_download_click', {
                  device_type: deviceType,
                  source: 'web_banner'
                });
              }
            }}
          >
            <FontAwesomeIcon icon={getStoreIcon()} />
            {getStoreText()}
          </DownloadButton>
          <CloseButton onClick={handleDismiss}>
            <FontAwesomeIcon icon={faTimes} />
          </CloseButton>
        </ButtonGroup>
      </BannerContent>
    </BannerContainer>
  );
};

export default MobileAppBanner; 